import {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type RefObject,
  type UIEventHandler,
} from "react";

export interface TimelineScrollHandle {
  scrollToEnd: (options?: { animated?: boolean }) => void;
  /** Puts the row of `messageId` at the top of the viewport. */
  scrollToMessage: (messageId: string) => void;
}

interface TimelineAnchor {
  id: string;
  viewportOffset: number;
}

const END_THRESHOLD_RATIO = 0.1;
const END_THRESHOLD_MIN_PX = 8;
const SMOOTH_SCROLL_SETTLE_MS = 1000;
const MESSAGE_TOP_MARGIN_PX = 12;

function distanceFromEnd(scroller: HTMLElement): number {
  return scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
}

function endThreshold(scroller: HTMLElement): number {
  return Math.max(END_THRESHOLD_MIN_PX, scroller.clientHeight * END_THRESHOLD_RATIO);
}

function findAnchorRow(scroller: HTMLElement, content: HTMLElement): HTMLElement | null {
  const top = scroller.scrollTop;
  for (const row of content.querySelectorAll<HTMLElement>("[data-timeline-row-id]")) {
    if (row.offsetTop + row.offsetHeight > top) {
      return row;
    }
  }
  return null;
}

export function useTimelineScroll({
  handleRef,
  onIsAtEndChange,
  layoutKey,
}: {
  handleRef: RefObject<TimelineScrollHandle | null> | undefined;
  onIsAtEndChange: (isAtEnd: boolean) => void;
  layoutKey: unknown;
}) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const isAtEndRef = useRef(true);
  const anchorRef = useRef<TimelineAnchor | null>(null);
  const smoothUntilRef = useRef(0);
  const onIsAtEndChangeRef = useRef(onIsAtEndChange);

  useLayoutEffect(() => {
    onIsAtEndChangeRef.current = onIsAtEndChange;
  }, [onIsAtEndChange]);

  const setIsAtEnd = useCallback((next: boolean) => {
    if (isAtEndRef.current === next) return;
    isAtEndRef.current = next;
    onIsAtEndChangeRef.current(next);
  }, []);

  const captureAnchor = useCallback(() => {
    const scroller = scrollerRef.current;
    const content = contentRef.current;
    if (!scroller || !content) return;
    const row = findAnchorRow(scroller, content);
    const id = row?.dataset.timelineRowId;
    anchorRef.current =
      row && id ? { id, viewportOffset: row.offsetTop - scroller.scrollTop } : null;
  }, []);

  const restoreAnchor = useCallback(() => {
    const scroller = scrollerRef.current;
    const content = contentRef.current;
    const anchor = anchorRef.current;
    if (!scroller || !content || !anchor) return;
    const row = content.querySelector<HTMLElement>(
      `[data-timeline-row-id="${CSS.escape(anchor.id)}"]`,
    );
    if (!row) return;
    const target = row.offsetTop - anchor.viewportOffset;
    if (Math.abs(target - scroller.scrollTop) >= 1) {
      scroller.scrollTop = target;
    }
  }, []);

  const pin = useCallback((animated: boolean) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const top = scroller.scrollHeight - scroller.clientHeight;
    if (animated) {
      smoothUntilRef.current = performance.now() + SMOOTH_SCROLL_SETTLE_MS;
      scroller.scrollTo({ top, behavior: "smooth" });
      return;
    }
    scroller.scrollTop = top;
  }, []);

  const onScroll = useCallback<UIEventHandler<HTMLDivElement>>(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const atEnd = distanceFromEnd(scroller) <= endThreshold(scroller);
    if (atEnd) {
      smoothUntilRef.current = 0;
    }
    if (atEnd || performance.now() >= smoothUntilRef.current) {
      setIsAtEnd(atEnd);
    }
    captureAnchor();
  }, [captureAnchor, setIsAtEnd]);

  const scrollerCallbackRef = useCallback(
    (element: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      scrollerRef.current = element;
      if (!element) return;
      const observer = new ResizeObserver(() => {
        if (isAtEndRef.current) {
          pin(false);
        }
        captureAnchor();
      });
      observer.observe(element);
      if (contentRef.current) {
        observer.observe(contentRef.current);
      }
      observerRef.current = observer;
    },
    [captureAnchor, pin],
  );

  useLayoutEffect(() => {
    if (isAtEndRef.current) {
      pin(false);
    } else {
      restoreAnchor();
    }
    captureAnchor();
  }, [layoutKey, captureAnchor, pin, restoreAnchor]);

  useImperativeHandle(
    handleRef,
    () => ({
      scrollToEnd: (options) => {
        setIsAtEnd(true);
        pin(options?.animated ?? false);
      },
      scrollToMessage: (messageId) => {
        const scroller = scrollerRef.current;
        const content = contentRef.current;
        if (!scroller || !content) return;
        const row = content.querySelector<HTMLElement>(
          `[data-message-id="${CSS.escape(messageId)}"]`,
        );
        if (!row) return;
        smoothUntilRef.current = 0;
        setIsAtEnd(false);
        scroller.scrollTop = Math.max(0, row.offsetTop - MESSAGE_TOP_MARGIN_PX);
        captureAnchor();
      },
    }),
    [captureAnchor, pin, setIsAtEnd],
  );

  return { scrollerRef: scrollerCallbackRef, contentRef, onScroll };
}
